<template>
  <block-item-card
    v-if="loaded"
    :min-height="minBlockHeight"
  >
    <v-row
      class="ma-0 py-2 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <div class="chart-container">
          <div class="chart-header">
            <div class="d-flex flex-column">
              <div
                class="d-flex flex-row"
                :class="'pa-2'"
                style="gap: 10px"
              >
                <div
                  class="d-flex flex-row"
                >
                  <h3
                    class="synthesis-inter-text synthesis-ui-grey-01--text d-flex align-center chart-title"
                  >
                    {{ title }}
                  </h3>
                </div>

                <div class="d-flex align-center chart-subtitle">
                  {{ subtitle }}
                </div>
              </div>
            </div>

            <asset-button-group
              :height="26"
              :items="assetGroups"
              :selected.sync="selectedBank"
              disable-dropdown-on-mobile
            />
          </div>
        </div>
        <e-chart
          :ref="`echart-social-media-mentions`"
          :autoresize="true"
          :option="socialMediaMentionsChartOptions"
          :style="`height: 400px;`"
          class="chart"
        />
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import API from '@/api/Api'
import Auth from '@/mixins/auth'
import { clone  }                         from '@/lib/utils/helper'
import { use } from 'echarts/core'
import { MarkAreaComponent, GraphicComponent, MarkLineComponent   } from 'echarts/components'
import SocialMediaMentionsResource from '@/api/resources/risk-liquidity/SocialMediaMentionsResource'
use([
  MarkAreaComponent,
  GraphicComponent,
  MarkLineComponent
])

export default {
  name      : 'SocialMediaMentions',
  components: {
    BlockItemCard,
    AssetButtonGroup
  },
  dataStore: {
    SocialMediaMentionsStore: 'RiskLiquidity.SocialMediaMentions'
  },
  mixins: [chartsColorMixin, Auth],
  props : {
    title: {
      type   : String,
      default: ''
    },
    subtitle: {
      type   : String,
      default: ''
    },
    isDashboard: {
      type   : Boolean,
      default: false
    }
  },
  data () {
    return {
      minBlockHeight: 350,
      selectedBank  : null
    }
  },

  computed: {
    socialMediaMentions () {
      return this.SocialMediaMentionsStore?.Data?.Data ?? {}
    },

    formattedDays () {
      return this.SocialMediaMentionsStore?.Data?.getFormattedDays(this.selectedBank) ?? []
    },

    loaded () {
      return this.SocialMediaMentionsStore?.Data?.Loaded ?? false
    },

    assetGroups () {
      const groups = this.SocialMediaMentionsStore?.Data?.AssetGroups ?? []

      return !this.isDashboard
        ? groups.filter(group => group.Value === 'currentMentions')
        : groups
    },

    thresholdMarkLines () {
      if (!this.loaded) return []
      const list = this.SocialMediaMentionsStore?.Data?.getThresholds(this.selectedBank) || []

      return list.map(t => ({
        yAxis          : Number(t.axisThreshold),
        name           : t.title,
        actualThreshold: Number(t.actualThreshold),
        lineStyle      : {
          color: this.themeColorRGBA('synthesis-ui-red-02', 1),
          width: 2,
          type : 'dashed'
        },
        label: {
          show           : true,
          position       : 'start',
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-02', 1),
          borderWidth    : 1,
          color          : this.themeColorRGBA('synthesis-ui-red-02', 1),
          fontWeight     : 500,
          fontSize       : 10,
          fill           : this.themeColorRGBA('synthesis-ui-orange-02'),
          textAlign      : 'center',
          backgroundColor: '#FFFFFF',
          borderRadius   : 4,
          padding        : [4, 6]
        }
      }))
    },

    thresholdMarkAreas () {
      // build contiguous areas between sorted thresholds
      const ts = (this.thresholdMarkLines || [])
        .map(d => d.yAxis)
        .filter(v => Number.isFinite(v))
        .sort((a, b) => a - b)

      const areas = []
      for (let i = 0; i < ts.length - 1; i++) {
        areas.push([
          { xAxis: 'min', yAxis: ts[i] },
          { xAxis: 'max', yAxis: ts[i + 1] }
        ])
      }
      return areas
    },

    socialMediaMentionsChartOptions () {
      if (!this.loaded) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null
            }
            const _chart = params[params.length - 1]

            const value = _chart.value
            return `<div style="font-weight: bold; margin-bottom: 8px;">Day: ${_chart.axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                        <span>${this.$t('RiskLiquidity.Section.RealTimeMonitoring.Blocks.SocialMediaMentions.Title')}: ${value}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 30,
          right       : 10,
          bottom      : 0,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDays,
          axisLabel: {
            margin      : 45,
            show        : true,
            color       : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize    : 12,
            showMaxLabel: true,
            interval    : 2
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : this.SocialMediaMentionsStore?.Data?.getMinThreshold(this.selectedBank) || 0,
          max      : this.SocialMediaMentionsStore?.Data?.getMaxThreshold(this.selectedBank) || 0,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value, index) => {
              if (this.SocialMediaMentionsStore?.Data?.EnableThresholdMap) {
                return this.SocialMediaMentionsStore?.Data?.getThresholdMap(this.selectedBank)?.[value] || value
              }
              return value
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },

        series: [
          {
            name: 'Social Media Index',
            type: 'line',
            data: (this.socialMediaMentions[this.selectedBank]?.items || []).map((i, idx) => {
              const isLast = idx === (this.socialMediaMentions[this.selectedBank]?.items || []).length - 1
              return {
                value       : i.mentions,
                symbol      : isLast ? 'image:///img/charts/dots/red-dot.png' : 'image:///img/charts/dots/green-dot.svg',
                symbolSize  : isLast ? 20 : 25,
                symbolOffset: isLast ? [0, 3] : [0, 6]
              }
            }),
            itemStyle: {
              color: this.themeColorRGBA('synthesis-ui-green-03')
            },
            lineStyle: {
              width: 3
            },
            symbol    : 'emptyCircle',
            symbolSize: 4,
            z         : 3,

            markArea: {
              silent   : true,
              // draw beneath the line but above splitLines
              // (if needed, you can also use z/zlevel here)
              itemStyle: {
                color: this.themeColorRGBA('synthesis-ui-red-02', 0.06) // soft fill
              },
              data: this.thresholdMarkAreas
            },
            markLine: {
              symbol   : 'none',
              silent   : true,
              animation: false,
              lineStyle: {
                color: this.themeColorRGBA('synthesis-ui-red-01'),
                type : 'dashed',
                width: 2
              },
              label: {
                show    : true,
                position: 'start',
                align   : 'left',
                offset  : [-25, 0],

                formatter: (p) => {
                  return '' + p?.data?.actualThreshold + ' - ' + (p?.data?.name || '')
                },
                backgroundColor: '#FFFFFF',
                borderColor    : this.themeColorRGBA('synthesis-ui-grey-02', 1),
                borderWidth    : 1,
                borderRadius   : 4,
                color          : this.themeColorRGBA('synthesis-ui-grey-01', 1),
                fontSize       : 10
              },
              data: this.thresholdMarkLines
            }

          }
        ]
      }
    },
    accountId () {
      return this.user?.selectedAccountId || null
    }
  },

  created () {
    this.getSocialMediaMentions()
  },
  methods: {
    async getSocialMediaMentions () {
      const response = await API.Resource.RiskLiquidity.GetSocialMediaMentions(this.accountId)
      this.handleResponse(response)
    },
    handleResponse (response) {
      const data = API.responseData(response)?.socialMediaMentions || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        const o = clone(data)
        this.$set(this.SocialMediaMentionsStore, 'Data', new SocialMediaMentionsResource(o))
        if (!this.selectedBank) {
          this.selectedBank = 'currentMentions'
        }
      } else {
        this.$bus.$emit('app:error', errors)
      }
    }
  }
}
</script>

<style scoped>

.gradient-grey-06-bg {
  background-color: var(--v-synthesis-ui-grey-06-base) !important;
}

.chart-title {
  font-size: 14px;
}

.chart-subtitle {
  color: var(--v-synthesis-ui-grey-01-base);
  font-size: 12px;
  position: relative;
}

.metric-label {
  font-size: 10px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-value {
  font-size: 12px;
  font-weight: 500;
}

.metric-value.positive {
  color: #4CAF50;
  font-size: 28px;
}

.metric-value.negative {
  color: #FF5722;
  font-size: 28px;
}

.chart-legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #666;
}

.legend-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
</style>
